import { createContext, useContext, useState, useRef, useEffect, useCallback } from "react";
import { PENALTIES, MOMENT_ORDER } from "@/data/content";

// ------------------------------------------------------------
// Match state: kickoff flag, active story section, shootout
// scoring, commentary overlay. Scoreboard reads everything
// from here so it stays consistent across the whole site.
// ------------------------------------------------------------

const SECTION_META = {
  kickoff: { minute: "00:00", score: [0, 0], mode: "match" },
  captain: { minute: "12:40", score: [1, 0], mode: "match" },
  reality: { minute: "27:00", score: [1, 0], mode: "match" },
  halftime: { minute: "45:00 · PAUSED", score: [1, 1], mode: "match" },
  search: { minute: "63:00", score: [1, 2], mode: "match" },
  building: { minute: "78:45", score: [1, 2], mode: "match" },
  fulltime: { minute: "90:00", score: [1, 2], mode: "match" },
  shootout: { mode: "penalties" },
  final: { mode: "fulltime" },
};

const INITIAL_SHOOTOUT = {
  status: "idle", // idle | playing | finished
  qIndex: 0,
  selected: null,
  revealed: false,
  results: [], // array of "advaith" | "life", one per answered penalty
};

const MatchContext = createContext(null);

export function MatchProvider({ children }) {
  const [kickedOff, setKickedOff] = useState(false);
  const [activeSection, setActiveSection] = useState("kickoff");
  const [shootout, setShootout] = useState(INITIAL_SHOOTOUT);
  const [commentaryId, setCommentaryId] = useState(null);
  const lenisRef = useRef(null);

  const scrollTo = useCallback((target) => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(target, {
        duration: 1.6,
        easing: (t) => 1 - Math.pow(1 - t, 4),
      });
    } else {
      document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const kickOff = useCallback(() => setKickedOff(true), []);

  const startShootout = useCallback(() => {
    setShootout({ ...INITIAL_SHOOTOUT, status: "playing" });
  }, []);

  const selectOption = useCallback((optionIndex) => {
    setShootout((s) => (s.revealed ? s : { ...s, selected: optionIndex }));
  }, []);

  // Applies the penalty's fixed outcome exactly once (double-click safe).
  const takeShot = useCallback(() => {
    setShootout((s) => {
      if (s.revealed || s.selected === null) return s;
      const outcome = PENALTIES[s.qIndex].outcome;
      return { ...s, revealed: true, results: [...s.results, outcome] };
    });
  }, []);

  const nextPenalty = useCallback(() => {
    setShootout((s) => {
      if (!s.revealed) return s;
      const next = s.qIndex + 1;
      if (next >= PENALTIES.length) {
        return { ...s, qIndex: next, status: "finished", selected: null, revealed: false };
      }
      return { ...s, qIndex: next, selected: null, revealed: false };
    });
  }, []);

  const restartShootout = useCallback(() => {
    setShootout({ ...INITIAL_SHOOTOUT, status: "playing" });
    scrollTo("#shootout");
  }, [scrollTo]);

  const openCommentary = useCallback((id) => setCommentaryId(id), []);
  const closeCommentary = useCallback(() => setCommentaryId(null), []);
  const nextCommentary = useCallback(() => {
    setCommentaryId((cur) => {
      const i = MOMENT_ORDER.indexOf(cur);
      return MOMENT_ORDER[(i + 1) % MOMENT_ORDER.length];
    });
  }, []);

  const advaithGoals = shootout.results.filter((r) => r === "advaith").length;
  const lifeGoals = shootout.results.filter((r) => r === "life").length;

  const meta = SECTION_META[activeSection] || SECTION_META.kickoff;

  // Scoreboard display derivation
  let board;
  if (meta.mode === "penalties") {
    board = {
      mode: "penalties",
      score: [advaithGoals, lifeGoals],
      clock:
        shootout.status === "finished"
          ? "PENALTIES"
          : `PENALTY ${Math.min(shootout.qIndex + 1, PENALTIES.length)}/${PENALTIES.length}`,
    };
  } else if (meta.mode === "fulltime") {
    board = { mode: "fulltime", score: [advaithGoals, lifeGoals], clock: "FULL TIME" };
  } else {
    board = kickedOff
      ? { mode: "match", score: meta.score, clock: meta.minute }
      : { mode: "match", score: [0, 0], clock: "00:00" };
  }

  const value = {
    kickedOff,
    kickOff,
    activeSection,
    setActiveSection,
    shootout,
    startShootout,
    selectOption,
    takeShot,
    nextPenalty,
    restartShootout,
    commentaryId,
    openCommentary,
    closeCommentary,
    nextCommentary,
    scrollTo,
    lenisRef,
    board,
  };

  return <MatchContext.Provider value={value}>{children}</MatchContext.Provider>;
}

export function useMatch() {
  return useContext(MatchContext);
}

// Registers a section so the scoreboard knows where the visitor is.
export function useSectionSpy(id) {
  const ref = useRef(null);
  const { setActiveSection } = useMatch();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setActiveSection(id);
      },
      { rootMargin: "-40% 0px -40% 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [id, setActiveSection]);
  return ref;
}
